import React, { useState } from 'react';
import cn from '@/utils/cn';
import { Controller, useForm } from 'react-hook-form';
import LocaleToggle from '@/components/common/LocaleToggle';
import { RequiredAsterisk } from '@/components/common/Forms/Utils';

const validFileExtensions = ['.png', '.jpg', '.gif'] as const;

const DEFAULT_VALUES = {
  definition_en: '',
  definition_es: 'SPANISH PLACEHOLDER',
  example_sentence_en: '',
  example_sentence_es: 'SPANISH PLACEHOLDER',
  image: null,
  term_en: '',
  term_es: 'SPANISH PLACEHOLDER',
};

type Form = {
  term_en: string;
  term_es: string;
  definition_en: string;
  definition_es: string;
  example_sentence_en: string;
  example_sentence_es: string;
  image?: File | null;
};

interface GlossaryTermProps {
  children: React.ReactNode;
  initialValues?: Partial<Form>;
  onSubmit: (data: FormData) => any;
  sectionId: string;
}

const GlossaryTermForm = ({ children, initialValues = {}, onSubmit, sectionId }: GlossaryTermProps) => {
  const [locale, setLocale] = useState<'en' | 'es'>('en');

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<Form>({
    defaultValues: {
      ...DEFAULT_VALUES,
      ...initialValues,
    },
  });

  const onFormSubmit = (data: Form) => {
    if (!sectionId) return;

    // eslint-disable-next-line @typescript-eslint/naming-convention
    const { term_en, term_es, definition_en, definition_es, example_sentence_en, example_sentence_es, image } = data;

    if (!term_en || !definition_en || !term_es || !definition_es) return;

    const formData = new FormData();
    formData.append('glossary_term[section_id]', sectionId);
    formData.append('glossary_term[term_en]', term_en);
    formData.append('glossary_term[definition_en]', definition_en);
    formData.append('glossary_term[term_es]', term_es);
    formData.append('glossary_term[definition_es]', definition_es);
    formData.append('glossary_term[example_sentence_en]', example_sentence_en);
    formData.append('glossary_term[example_sentence_es]', example_sentence_es);
    if (image) formData.append('glossary_term[glossary_image]', image);

    onSubmit(formData);

    reset({
      definition_en: '',
      definition_es: 'SPANISH PLACEHOLDER',
      example_sentence_en: '',
      example_sentence_es: 'SPANISH PLACEHOLDER',
      image: null,
      term_en: '',
      term_es: 'SPANISH PLACEHOLDER',
    });
  };

  return (
    <div>
      <LocaleToggle
        btnClasses="tw-block tw-mb-[1.5rem] tw-overflow-hidden tw-text-tci-purple"
        isSaving={false}
        locale={locale}
        setLocale={setLocale}
      />
      <form className="tw-flex tw-flex-col" onSubmit={handleSubmit(onFormSubmit)}>
        <label className="tw-flex tw-mb-[1.5rem]">
          <div className="tw-flex tw-mr-[20px] tw-w-[6rem] tw-pt-[0.5rem]">
            <p className="tw-font-bold tw-text-[16px]"> Term </p>
            <RequiredAsterisk />
          </div>
          <input
            type="text"
            {...register('term_en', { required: true })}
            className={cn('tw-w-full tw-rounded-lg tw-h-[2.5rem] tw-px-[1rem] tw-border-[1px] tw-border-solid', {
              'tw-border-grey': !errors.term_en,
              'tw-border-red': errors.term_en,
              'tw-hidden': locale === 'es',
            })}
          />
          <input
            type="text"
            {...register('term_es', { required: true })}
            className={cn('tw-w-full tw-rounded-lg tw-h-[2.5rem] tw-px-[1rem] tw-border-[1px] tw-border-solid', {
              'tw-border-grey': !errors.term_es,
              'tw-border-solid tw-border-red': errors.term_es,
              'tw-hidden': locale === 'en',
            })}
          />
        </label>
        <label className="tw-flex tw-mb-[1.5rem]">
          <div className="tw-flex tw-mr-[20px] tw-w-[6rem] tw-pt-[0.5rem]">
            <p className="tw-font-bold tw-text-[16px]"> Definition </p>
            <RequiredAsterisk />
          </div>
          <textarea
            {...register('definition_en', { required: true })}
            className={cn(
              'tw-w-full tw-resize-none tw-rounded-lg tw-h-[5rem] tw-pt-2 tw-px-[1rem]',
              'tw-border-[1px] tw-border-solid', {
                'tw-border-grey': !errors.definition_en,
                'tw-border-red': errors.definition_en,
                'tw-hidden': locale === 'es',
              },
            )}
          />
          <textarea
            {...register('definition_es', { required: true })}
            className={cn(
              'tw-w-full tw-resize-none tw-rounded-lg tw-h-[5rem] tw-pt-2 tw-px-[1rem]',
              'tw-border-[1px] tw-border-solid', {
                'tw-border-grey': !errors.definition_es,
                'tw-border-red': errors.definition_es,
                'tw-hidden': locale === 'en',
              },
            )}
          />
        </label>
        <div className="tw-flex tw-mb-[1.5rem]">
          <p className="tw-font-bold tw-w-[6rem] tw-text-[16px] tw-mr-1"> Add Image </p>
          <Controller
            control={control}
            name="image"
            render={({ field }) => (
              <input
                accept={validFileExtensions.join(',')}
                type="file"
                onChange={e => field.onChange(e.target.files[0])}
              />
            )}
          />
        </div>

        <label className="tw-flex tw-mb-[1.5rem]">
          <div className="tw-flex tw-mr-[20px] tw-w-[6rem] tw-pt-[0.5rem]">
            <p className="tw-font-bold tw-text-[16px]"> Example sentence </p>
          </div>
          <textarea
            {...register('example_sentence_en')}
            className={cn('tw-w-full tw-resize-none tw-rounded-lg tw-h-[5rem] tw-pt-2 tw-px-[1rem]', {
              'tw-border-grey': !errors.example_sentence_en,
              'tw-border-red': errors.example_sentence_en,
              'tw-hidden': locale === 'es',
            })}
          />
          <textarea
            {...register('example_sentence_es')}
            className={cn('tw-w-full tw-resize-none tw-rounded-lg tw-h-[5rem] tw-pt-2 tw-px-[1rem]', {
              'tw-border-grey': !errors.example_sentence_es,
              'tw-border-solid tw-border-red': errors.example_sentence_es,
              'tw-hidden': locale === 'en',
            })}
          />
        </label>
        {children}
        {Object.keys(errors).length > 0 && (
          <p className="tw-text-red tw-absolute tw-bottom-4">Please double-check the English and Spanish fields.</p>
        )}
      </form>
    </div>
  );
};

export default GlossaryTermForm;
